import {
  Flex,
  Icon,
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  Portal,
  Spacer,
  Text,
} from '@chakra-ui/react';
import { IconMapPin } from 'central-icons/IconMapPin';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ScreenerTableRow } from '../../DataProvider/utils/transform-screener-table-data';
import { EmailPopover } from './ScreenerTablePopover/EmailPopover';
import { CompanyIcon } from './ScreenerTablePopover/icons/company-icon';
import { EmailIcon } from './ScreenerTablePopover/icons/email-icon';
import { LinkedinIcon } from './ScreenerTablePopover/icons/linkedin-icon';

const POPOVER_OPEN_DELAY_MS = 500;
const POPOVER_CLOSE_DELAY_MS = 100;

const mergeRefs = <T,>(
  refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> => {
  return (node: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null && typeof ref === 'object' && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
};

export interface ScreenerTablePopoverProps {
  data: ScreenerTableRow;
  children: JSX.Element | JSX.Element[];
}

export const ScreenerTablePopover = ({
  data,
  children,
}: ScreenerTablePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLTableRowElement | null>(null);
  const hiddenAnchorRef = useRef<HTMLDivElement | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (anchorRef.current && hiddenAnchorRef.current && isOpen) {
      const rect = anchorRef.current.getBoundingClientRect();
      hiddenAnchorRef.current.style.position = 'fixed';
      hiddenAnchorRef.current.style.top = `${rect.top}px`;
      hiddenAnchorRef.current.style.left = `${rect.left}px`;
      hiddenAnchorRef.current.style.width = `${rect.width}px`;
      hiddenAnchorRef.current.style.height = `${rect.height}px`;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  if (!data) return children;

  const {
    year_founded,
    phone_number,
    linkedin_url,
    email = 'Not available',
    description,
    short_name = '',
    hq_metro_area,
    hq_country,
    website,
  } = data;

  const href = website ? `https://${website}` : undefined;
  const linkProps = href
    ? { cursor: 'pointer', _hover: { textDecoration: 'underline' } }
    : {};

  const childElement = Array.isArray(children) ? children[0] : children;

  if (
    !React.isValidElement<
      React.HTMLAttributes<HTMLTableRowElement> & {
        ref?: React.Ref<HTMLTableRowElement>;
      }
    >(childElement)
  ) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const originalRef = childElement.props.ref;

  const triggerElement = React.cloneElement(childElement, {
    ref: mergeRefs([
      (node: HTMLTableRowElement | null) => {
        anchorRef.current = node;
      },
      originalRef,
    ]),
    onMouseEnter: () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      if (hiddenAnchorRef.current && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        hiddenAnchorRef.current.style.position = 'fixed';
        hiddenAnchorRef.current.style.top = `${rect.top}px`;
        hiddenAnchorRef.current.style.left = `${rect.left}px`;
        hiddenAnchorRef.current.style.width = `${rect.width}px`;
        hiddenAnchorRef.current.style.height = `${rect.height}px`;
      }
      openTimeoutRef.current = setTimeout(() => {
        setIsOpen(true);
      }, POPOVER_OPEN_DELAY_MS);
    },
    onMouseLeave: () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, POPOVER_CLOSE_DELAY_MS);
    },
  });

  return (
    <>
      {triggerElement}
      {createPortal(
        <Popover
          variant="tableTooltip"
          placement="bottom"
          isOpen={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          closeOnBlur={false}
          isLazy
        >
          <PopoverAnchor>
            <div
              ref={hiddenAnchorRef}
              style={{
                position: 'fixed',
                pointerEvents: 'none',
                visibility: 'hidden',
              }}
            />
          </PopoverAnchor>
          <Portal>
            <PopoverContent
              onMouseEnter={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                  closeTimeoutRef.current = null;
                }
                if (openTimeoutRef.current) {
                  clearTimeout(openTimeoutRef.current);
                }
                setIsOpen(true);
              }}
              onMouseLeave={() => {
                if (openTimeoutRef.current) {
                  clearTimeout(openTimeoutRef.current);
                  openTimeoutRef.current = null;
                }
                closeTimeoutRef.current = setTimeout(() => {
                  setIsOpen(false);
                }, POPOVER_CLOSE_DELAY_MS);
              }}
            >
              <PopoverArrow />
              <PopoverHeader as="div">
                <Flex alignItems="center" gap={2}>
                  <a href={href} target="_blank" rel="noreferrer">
                    <IconButton
                      variant="solid"
                      colorScheme="grey"
                      aria-label="Company"
                      icon={<CompanyIcon height={40} width={40} bottom={3} />}
                      size="md"
                    />
                  </a>
                  <Flex direction="column" w="full">
                    <Flex direction="row" alignItems="center">
                      <a href={href} target="_blank" rel="noreferrer">
                        <Text
                          as="span"
                          color="white"
                          fontSize="sm"
                          fontWeight="semibold"
                          {...linkProps}
                        >
                          {short_name}
                        </Text>
                      </a>
                      <Spacer />
                      <Flex gap={1} alignItems="center">
                        {linkedin_url && (
                          <IconButton
                            isRound
                            variant="solid"
                            colorScheme="grey"
                            aria-label="LinkedIn"
                            size="xs"
                            icon={<LinkedinIcon />}
                            onClick={() => {
                              window.open(
                                `${linkedin_url}`,
                                '_blank',
                                'noopener,noreferrer'
                              );
                            }}
                          />
                        )}
                        {email && (
                          <EmailPopover
                            emails={{
                              email: String(email),
                            }}
                            trigger={
                              <IconButton
                                isRound
                                variant="solid"
                                colorScheme="grey"
                                aria-label="Email"
                                icon={<EmailIcon />}
                                size="xs"
                              />
                            }
                          />
                        )}
                      </Flex>
                    </Flex>
                    <Flex direction="row" alignItems="center" mt="-1px">
                      <Text color="white" fontSize="xs" opacity={0.6}>
                        <Icon as={IconMapPin} boxSize={2.5} mr={1} />
                        {[hq_metro_area, hq_country].join(', ') ||
                          'Not available'}
                      </Text>
                      <Spacer />
                      <Flex direction="column">
                        <Text color="white" fontSize="xs" opacity={0.6}>
                          {year_founded || phone_number}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </PopoverHeader>
              <PopoverBody as="div" pb={4}>
                <Text as="span" color="white" fontSize="xs" opacity={0.6}>
                  {description || 'No description available'}
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>,
        document.body
      )}
    </>
  );
};
